import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["startDate", "term", "computedEndDate"]

  connect() {
    // store anchor day per field
    this.anchorDays = {}
    if (this.startDateTarget.value) {
      this.anchorDays["startDate"] = new Date(this.startDateTarget.value).getDate()
    }

    this.computeEndDate()
  }

  startDateChanged() {
    if (this.startDateTarget.value) {
      this.anchorDays["startDate"] = new Date(this.startDateTarget.value).getDate()
    }
    this.computeEndDate()
  }

  termChanged() {
    if (!this.hasTermTarget) return

    const months = this.parseTermToMonths(this.termTarget.value)

    if (this.termTarget.value && months === null) {
      this.termTarget.classList.add("input-invalid")
      this.termTarget.title = "Invalid format, use e.g. 20y6m, 5y, or 18m"
    } else {
      this.termTarget.classList.remove("input-invalid")
      this.termTarget.title = ""
    }

    this.computeEndDate()
  }

  adjustDate(event) {
    const adjustment = event.currentTarget.dataset.adjustment
    const targetName = event.currentTarget.dataset.target
    const targetInput = this[`${targetName}Target`]
    if (!targetInput.value) return

    const months = this.parseTermToMonths(adjustment)
    if (months === null) return

    const date = new Date(targetInput.value)
    const anchorDay = this.anchorDays[targetName] || date.getDate()

    const newDate = this.addMonthsClamped(date, months, anchorDay)

    targetInput.value = this.formatDate(newDate)
    this.anchorDays[targetName] = anchorDay // remember for next adjustment

    // End date will be computed when the input event is dispatched
    // this.computeEndDate()

    targetInput.dispatchEvent(new Event("input", { bubbles: true }))
  }

  setTerm(event) {
    if (!this.hasTermTarget) return

    const term = event.currentTarget.dataset.value
    this.termTarget.value = term
    this.termChanged()
  }

  computeEndDate() {
    // console.log("Computing end date with start date:", this.startDateTarget.value, "and term:", this.termTarget.value)

    if (!this.hasComputedEndDateTarget) return
    if (!this.hasTermTarget) return

    if (!this.startDateTarget.value) return

    const startDate = new Date(this.startDateTarget.value)
    const months = this.parseTermToMonths(this.termTarget.value)

    if (months === null || months <= 0) {
      this.computedEndDateTarget.value = ""
      return
    }

    // use anchor day for startDate
    const anchorDay = this.anchorDays["startDate"] || startDate.getDate()
    const endDate = this.addMonthsClamped(startDate, months, anchorDay)
    endDate.setDate(endDate.getDate() - 1) // inclusive

    this.computedEndDateTarget.value = this.formatDate(endDate)
  }

  addMonthsClamped(date, monthsToAdd, anchorDay) {
    const year = date.getFullYear()
    const month = date.getMonth()

    const newMonthIndex = month + monthsToAdd
    const newDate = new Date(year, newMonthIndex, anchorDay)

    // clamp to last day of month
    const lastDay = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate()
    if (newDate.getDate() > lastDay) newDate.setDate(lastDay)

    return newDate
  }

  parseTermToMonths(input) {
    if (!input) return null
    const trimmed = input.trim()
    const sign = trimmed.startsWith("-") ? -1 : 1
    const clean = trimmed.replace(/^[-+]/, "")

    const regex = /^(?:(\d+)\s*y)?\s*(?:(\d+)\s*m)?$/i
    const match = clean.match(regex)
    if (!match) return null
    if (!match[1] && !match[2]) return null

    const years = parseInt(match[1] || 0, 10)
    const months = parseInt(match[2] || 0, 10)

    return sign * (years * 12 + months)
  }

  formatDate(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }
}
